import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import TextInput from "../components/forms/TextInput";

const shellStyle = {
  width: "100%",
  maxWidth: "480px",
  margin: "0 auto",
  paddingTop: "var(--space-6)",
};

const loginLinkStyle = {
  color: "var(--color-accent)",
  fontWeight: "var(--font-weight-semibold)",
  textDecoration: "none",
};

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    phone_number: "",
    name: "",
    surname: "",
    password: "",
  });

  const handleInput = (e) => {
    const { name, value } = e.target;

    if (name === "phone_number" || name === "password") {
      const digitsOnly = value.replace(/\D/g, "");
      setForm((prev) => ({ ...prev, [name]: digitsOnly }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const phone = form.phone_number.replace(/\D/g, "");
    const pwd = form.password.trim();

    if (!phone) return t("register.errors.missing_phone");
    if (!/^\d{7,15}$/.test(phone)) {
      return t("register.errors.invalid_phone");
    }
    if (!form.name.trim()) return t("register.errors.missing_name");
    if (!form.surname.trim()) return t("register.errors.missing_surname");
    if (!/^\d{4}$/.test(pwd)) return t("register.errors.invalid_pin");

    return null;
  };

  const onRegister = async () => {
    if (submitting) return;

    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    const payload = {
      phone_number: form.phone_number.replace(/\D/g, ""),
      name: form.name.trim(),
      surname: form.surname.trim(),
      password: form.password.trim(),
    };

    try {
      setSubmitting(true);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/register-phone`,
        payload
      );

      alert(t("register.success"));

      navigate("/login", {
        replace: true,
        state: { phoneNumber: payload.phone_number },
      });
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        t("register.errors.failed");
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StandardScreen background="default">
      <ScreenContent>
        <div style={shellStyle}>
          <Card
            title={t("register.card_title")}
            subtitle={t("register.card_subtitle")}
          >
            <Stack gap="4">
              <FormField label={t("register.phone_label")}>
                <TextInput
                  name="phone_number"
                  type="tel"
                  inputMode="tel"
                  placeholder={t("register.phone_placeholder")}
                  value={form.phone_number}
                  onChange={handleInput}
                  autoComplete="tel"
                  disabled={submitting}
                />
              </FormField>

              <FormField label={t("register.name_label")}>
                <TextInput
                  name="name"
                  type="text"
                  placeholder={t("register.name_placeholder")}
                  value={form.name}
                  onChange={handleInput}
                  autoComplete="given-name"
                  disabled={submitting}
                />
              </FormField>

              <FormField label={t("register.surname_label")}>
                <TextInput
                  name="surname"
                  type="text"
                  placeholder={t("register.surname_placeholder")}
                  value={form.surname}
                  onChange={handleInput}
                  autoComplete="family-name"
                  disabled={submitting}
                />
              </FormField>

              <FormField label={t("register.pin_label")}>
                <TextInput
                  name="password"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={t("register.pin_placeholder")}
                  value={form.password}
                  onChange={handleInput}
                  maxLength={4}
                  autoComplete="new-password"
                  disabled={submitting}
                />
              </FormField>

              <Button
                onClick={onRegister}
                disabled={submitting}
                fullWidth
                size="lg"
              >
                {submitting
                  ? t("register.loading_button")
                  : t("register.submit_button")}
              </Button>

              <p
                style={{
                  margin: 0,
                  textAlign: "center",
                  fontSize: "var(--font-size-md)",
                }}
              >
                {t("register.login_prompt")}{" "}
                <Link to="/login" style={loginLinkStyle}>
                  {t("register.login_link")}
                </Link>
              </p>
            </Stack>
          </Card>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default Register;